/**
 * Backend beneficial-ownership API — typed TanStack Query hooks.
 *
 * Wraps `GET /v1/trusts/{trustId}/beneficial-ownership` (list per trust) plus
 * the register mutations:
 *   - POST   /v1/trusts/{trustId}/beneficial-ownership   (add owner)
 *   - PATCH  /v1/beneficial-ownership/{id}?trustId={trustId} (edit owner)
 *   - DELETE /v1/beneficial-ownership/{id}?trustId={trustId} (soft-delete)
 *   - POST   /v1/beneficial-ownership/{id}/lodge-master?trustId={trustId}
 *   - GET    /v1/trusts/{trustId}/beneficial-ownership/register-export
 *            (LOD-01: the register as the Master's own 23-column sheet;
 *            .xlsx by default, ?format=csv for the original text file)
 * (The per-owner detail GET + useBeneficialOwner hook were killed
 * 2026-07-09 — wiring-map ruling; edit-mode status seeding reads the list.)
 *
 * For non-admin viewers, the backend redacts PII fields (idNumber,
 * passportNumber). Hook types reflect that — those fields are optional.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './client';
// The LEAF, not `acting/context` - importing the React surface here would pull
// `use-session -> auth-context -> query-client` back into the API layer. Same
// reason `beneficiaries.ts` imports it this way for its own CSV download.
import { bearerTokenForContext } from '../acting/bearer';

export type BoCategory = 'settlor' | 'trustee' | 'beneficiary' | 'protector' | 'other';
export type BoSubClause = 'a' | 'b' | 'c' | 'd' | 'e';

export interface BeneficialOwnerView {
  id: string;
  tenantId: string;
  trustId: string;
  ownerType: 'natural-person' | 'juristic-person';
  fullName: string;
  category: BoCategory;
  tpcaSubClause: BoSubClause;
  dateOfBirth?: string;
  nationality?: string;
  countryOfTaxResidence?: string;
  // ── Reg 3C(1) particulars (TPCA reg 3C(1)) ─────────────────────────────────
  /** Citizenship (ISO-3166-1 alpha-2), distinct from nationality. All viewers. */
  citizenship?: string;
  /** ISO date the person became a beneficial owner. All viewers. */
  becameBoOn?: string;
  /** ISO date the person ceased to be a beneficial owner (if applicable). */
  ceasedBoOn?: string;
  /** Vault document id of the certified ID copy (reg 3C(3)). All viewers. */
  certifiedIdDocumentId?: string;
  /** Admin-only — redacted for non-admin viewers. */
  idNumber?: string;
  /** Admin-only — redacted for non-admin viewers. */
  passportNumber?: string;
  /** Tax number (reg 3C(1)) — admin-only, redacted for non-admin viewers. */
  taxNumber?: string;
  /** Residential address (reg 3C(1)) — admin-only PII. */
  residentialAddress?: string;
  /** Address for service of notices (reg 3C(1)) — admin-only PII. */
  serviceAddress?: string;
  /** Contact email (reg 3C(1)) — admin-only PII. */
  email?: string;
  /** Contact phone (reg 3C(1)) — admin-only PII. */
  phone?: string;
  /** Minor case: guardian particulars (reg 3C(1)) — admin-only PII. */
  guardianFullName?: string;
  guardianRelationship?: string;
  guardianContact?: string;
  /** True when no MANDATORY reg 3C(1) particular is missing for lodgement.
   *  Optional here so older fixtures compile; the backend always returns it. */
  regComplete?: boolean;
  /** Machine-readable keys of the missing mandatory reg 3C(1) particulars. */
  regMissingFields?: string[];
  /** FICA / KYC verification status from the ThisIsMe webhook (#30). Screening
   *  metadata (not subject PII) — present for all viewers. */
  ficaStatus?: 'pending' | 'verified' | 'rejected';
  /** KYC rejection reason — present only when ficaStatus === 'rejected'. The
   *  admin/clients "People" card surfaces this so a rejected owner can be
   *  chased (mirrors the COMBO-020 ownership-tree reason). */
  ficaRejectReason?: string;
  // ── Manual FICA capture (RULING 2026-09-08, Paul: option A) ────────────────
  // Where the status CAME FROM travels with the status, everywhere, so no
  // screen can show a firm's own attestation as an identity provider's
  // verification. Absent on rows written before 8 September 2026.
  /** `'manual'` = a named operator at the firm confirmed they saw the
   *  documents. `'vendor'` = an identity provider returned the verdict. */
  ficaSource?: 'manual' | 'vendor';
  /** Who recorded the manual check, and when. */
  ficaConfirmedByName?: string;
  ficaConfirmedAt?: string;
  /** What they attested to, in its named parts. */
  ficaIdentityDocumentSeen?: boolean;
  ficaAddressProofSeen?: boolean;
  /** A manual check the firm later withdrew. Present on a row back at
   *  outstanding, so the screen says a correction was made rather than
   *  pretending the person was never checked. */
  ficaWithdrawnAt?: string;
  ficaWithdrawnByName?: string;
  ficaWithdrawnReason?: string;
  isLodgementLocked: boolean;
  lodgementVersion: number;
  pendingChangesSinceLodgement: boolean;
  status: 'active' | 'pending-review' | 'archived';
  createdAt: string;
  updatedAt: string;
}

export interface BoListResponse {
  beneficialOwners: BeneficialOwnerView[];
  correlationId: string;
}

/** @param opts.enabled Pass `false` to skip the call entirely. The endpoint
 *  serves the `people` surface, so a beneficiary-tier member is refused it —
 *  callers gate on `useTrustSurfaces(id).can('people')` so the request is never
 *  MADE rather than made and 403'd. */
export function useBeneficialOwnership(
  trustId: string | undefined,
  opts: { enabled?: boolean } = {},
) {
  return useQuery<BoListResponse>({
    queryKey: ['beneficial-ownership', 'list', trustId ?? null],
    queryFn: ({ signal }) =>
      api<BoListResponse>(
        `/api/v1/trusts/${encodeURIComponent(trustId!)}/beneficial-ownership`,
        { signal },
      ),
    enabled: (opts.enabled ?? true) && !!trustId,
  });
}

// ── Register mutations ───────────────────────────────────────────────────────

/** Invalidate every read of the register: the flat BO list hook here
 *  AND the grouped TPCA tree (`useTrustOwnership`, keyed `['trusts',
 *  'ownership', trustId]`) that the ownership screen renders. */
function invalidateRegister(
  qc: ReturnType<typeof useQueryClient>,
  trustId: string,
) {
  void qc.invalidateQueries({ queryKey: ['beneficial-ownership'] });
  void qc.invalidateQueries({ queryKey: ['trusts', 'ownership', trustId] });
}

/** Body of POST /v1/trusts/{trustId}/beneficial-ownership — mirrors the
 *  backend CreateBoSchema exactly (strict: unknown keys are rejected, and
 *  there is NO source/provenance field — a manually captured owner is not
 *  distinguishable from an ingested one on the record itself). */
export interface CreateBeneficialOwnerRequest {
  ownerType: 'natural-person' | 'juristic-person';
  fullName: string;
  category: BoCategory;
  tpcaSubClause: BoSubClause;
  dateOfBirth?: string;
  /** ISO 3166-1 alpha-2 (the backend enforces length 2). */
  nationality?: string;
  /** ISO 3166-1 alpha-2 (the backend enforces length 2). */
  countryOfTaxResidence?: string;
  idNumber?: string;
  passportNumber?: string;
  // ── Reg 3C(1) particulars (mirror the backend CreateBoSchema) ──────────────
  /** ISO 3166-1 alpha-2 (the backend enforces length 2). */
  citizenship?: string;
  residentialAddress?: string;
  serviceAddress?: string;
  email?: string;
  phone?: string;
  taxNumber?: string;
  /** ISO date YYYY-MM-DD (the backend enforces the shape). */
  becameBoOn?: string;
  /** ISO date YYYY-MM-DD (the backend enforces the shape). */
  ceasedBoOn?: string;
  guardianFullName?: string;
  guardianRelationship?: string;
  guardianContact?: string;
  certifiedIdDocumentId?: string;
}

/** The created row. The create handler additionally returns the sanctions/PEP
 *  screening outcome it ran inline (fields the list projection omits). */
export interface CreatedBeneficialOwnerView extends BeneficialOwnerView {
  /** 'not_screened' = the owner was added but NO sanctions/PEP screening ran
   *  (no provider configured). It is NOT a clean verdict and blocks Master
   *  lodgement until screening completes. 'pending' = a prior verdict was
   *  invalidated and the cascade will re-screen. */
  screeningStatus?: 'clean' | 'flagged' | 'not_screened' | 'pending';
  /** Present only for an actual screening outcome (absent for not_screened). */
  screeningRiskTier?: 'low' | 'medium' | 'high' | 'critical';
}

export interface CreateBeneficialOwnerResponse {
  beneficialOwner: CreatedBeneficialOwnerView;
  correlationId: string;
}

/**
 * Add a beneficial owner to a trust's TPCA s1 register.
 * POST /v1/trusts/{trustId}/beneficial-ownership.
 *
 * Step-up gated (the global StepUpInterceptor handles the challenge; call
 * sites surface pending/denied via the error toast). New rows land unlocked
 * (`isLodgementLocked: false, lodgementVersion: 0`) — /lodge-master flips
 * the lock. The backend screens the subject inline; the response carries
 * `screeningStatus` / `screeningRiskTier`.
 */
export function useCreateBeneficialOwner(trustId: string) {
  const qc = useQueryClient();
  return useMutation<
    CreateBeneficialOwnerResponse,
    ApiError,
    CreateBeneficialOwnerRequest
  >({
    mutationFn: (body) =>
      api<CreateBeneficialOwnerResponse>(
        `/api/v1/trusts/${encodeURIComponent(trustId)}/beneficial-ownership`,
        { method: 'POST', body },
      ),
    onSuccess: () => invalidateRegister(qc, trustId),
  });
}

/** Body of PATCH /v1/beneficial-ownership/{id} — every CreateBoSchema field,
 *  optional, plus the record-lifecycle `status`. Send ONLY changed fields:
 *  PII fields are redacted for non-admin viewers, so an unconditional
 *  send-the-whole-form would overwrite a real idNumber with ''. */
export interface PatchBeneficialOwnerRequest
  extends Partial<CreateBeneficialOwnerRequest> {
  status?: 'active' | 'pending-review' | 'archived';
}

export interface PatchBeneficialOwnerResponse {
  beneficialOwner: BeneficialOwnerView;
  correlationId: string;
}

/**
 * Edit a beneficial-owner record.
 * PATCH /v1/beneficial-ownership/{id}?trustId={trustId}. Step-up gated.
 *
 * Lodgement-lock semantics (REQ-DATA-014, from the handler header): the lock
 * does NOT prevent edits — editing a locked row sets
 * `pendingChangesSinceLodgement: true` so the register needs re-lodgement.
 * Call sites should surface that state after a locked-row edit.
 */
export function usePatchBeneficialOwner(trustId: string) {
  const qc = useQueryClient();
  return useMutation<
    PatchBeneficialOwnerResponse,
    ApiError,
    { id: string; patch: PatchBeneficialOwnerRequest }
  >({
    mutationFn: ({ id, patch }) => {
      const search = new URLSearchParams({ trustId });
      return api<PatchBeneficialOwnerResponse>(
        `/api/v1/beneficial-ownership/${encodeURIComponent(id)}?${search.toString()}`,
        { method: 'PATCH', body: patch },
      );
    },
    onSuccess: () => invalidateRegister(qc, trustId),
  });
}

/**
 * Remove a beneficial owner from the register (SOFT delete — the record is
 * retained with a deletion marker; it simply leaves the active register).
 * DELETE /v1/beneficial-ownership/{id}?trustId={trustId}. Step-up gated.
 *
 * Contract (handler header): lodged/locked rows CANNOT be deleted — 409
 * BO_LOCKED "Archive via PATCH status=archived instead" (REQ-DATA-014).
 * Success is 204 No Content.
 */
export function useDeleteBeneficialOwner(trustId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) => {
      const search = new URLSearchParams({ trustId });
      return api<void>(
        `/api/v1/beneficial-ownership/${encodeURIComponent(id)}?${search.toString()}`,
        { method: 'DELETE' },
      );
    },
    onSuccess: () => invalidateRegister(qc, trustId),
  });
}

/** The lodgement-attempt row created by /lodge-master — mirrors the backend
 *  BoLodgementAttemptDoc fields the response carries. Its `status:
 *  'awaiting-submission'` card is what the master-lodgements Kanban renders. */
export interface BoLodgementAttempt {
  id: string;
  trustId: string;
  status:
    | 'drafting'
    | 'awaiting-submission'
    | 'awaiting-master-response'
    | 'lodged'
    | 'rejected';
  statusChangedAt: string;
  snapshotIds: string[];
  initiatedByUserId: string;
  /** RULING 2026-08-23 (Paul): `'not_screened'` when this register was filed
   *  while no sanctions/PEP vendor was configured. Absent otherwise (the AML
   *  gate was satisfied). Never carries a 'clean'/'passed' value — it exists
   *  only to report that a filing went through UNSCREENED. */
  screeningStatus?: 'not_screened';
  /** Machine-readable cause for `screeningStatus: 'not_screened'`. */
  screeningDeferredReason?: 'screening_deferred_no_provider';
  /** The active owner ids the deferral covers. */
  screeningDeferredIds?: string[];
}

export interface LodgeMasterResponse {
  attempt: BoLodgementAttempt;
  /** How many BO rows were flipped to locked (== snapshot size on success). */
  lockedCount: number;
  /** null when the register is in the caller's own tenant; the stewarded client
   *  tenant when the firm filed cross-book on a live `manage` grant. */
  clientTenantId: string | null;
  clientDisplayName: string | null;
  correlationId: string;
}

/**
 * Lodge the trust's TPCA s1 register with the Master (creates a
 * BoLodgementAttemptDoc + locks every active BO row).
 * POST /v1/beneficial-ownership/{id}/lodge-master?trustId={trustId}.
 *
 * Contract notes quoted from the handler header:
 *   - the `{id}` path param is a SENTINEL — "any BO id in the trust"; the
 *     action is trust-scoped and snapshots the FULL active BO set.
 *   - roles: admin / compliance / rm / super_admin (403
 *     NOT_AUTHORISED_TO_LODGE otherwise) + step-up.
 *   - 409 NO_ACTIVE_BENEFICIAL_OWNERS · 409 BENEFICIAL_OWNER_SCREENING_FLAGGED
 *     (AML fail-closed; `flaggedIds` names the blockers) · 502
 *     LODGE_MASTER_PARTIAL (`attemptId` created but not all rows locked —
 *     recover on the lodgement board).
 *
 * ── `useOperatorSession: true` (2026-08-22) — this is the whole fix ──────────
 * This call MUST travel on the operator's OWN Supabase session, never on an
 * act-as token, and that is not an optimisation: it is the difference between
 * the button working and the button being permanently dead.
 *
 * A firm user opens a client trust via act-as, so by default every call from
 * this screen rides the firm-act token. But `mintFirmActToken` deliberately
 * never stamps `mfa_challenge_at` (the C2 wall), and the handler requires
 * step-up — so the POST 403'd STEP_UP_REQUIRED, the global step-up modal
 * challenged, minted a fresh SUPABASE token, and replayed the mutation on the
 * unchanged act-as token, which 403'd again. An infinite MFA loop the operator
 * reads as "the Lodge with Master button does nothing". Nothing anywhere could
 * create a lodgement attempt (E2E 2026-08-21/22).
 *
 * Riding the operator session instead means the firm admin's real MFA challenge
 * satisfies step-up, and the backend resolves the client trust across the
 * firm's grant-verified book (`findTrustAcrossBook`) and writes into the client
 * tenant — the same cross-book shape the board's advance/reject already use.
 * For a client-tenant user with no act-as active this flag is a no-op: they
 * already are the operator session.
 */
export function useLodgeMaster(trustId: string) {
  const qc = useQueryClient();
  return useMutation<LodgeMasterResponse, ApiError, { anyBoId: string }>({
    mutationFn: ({ anyBoId }) => {
      const search = new URLSearchParams({ trustId });
      return api<LodgeMasterResponse>(
        `/api/v1/beneficial-ownership/${encodeURIComponent(anyBoId)}/lodge-master?${search.toString()}`,
        { method: 'POST', useOperatorSession: true },
      );
    },
    onSuccess: () => {
      invalidateRegister(qc, trustId);
      // The new attempt is a card on the admin lodgement board.
      void qc.invalidateQueries({ queryKey: ['admin', 'master-lodgements'] });
    },
  });
}

// ── Manual FICA capture (RULING 2026-09-08, Paul: option A) ───────────────────

/** The two named parts of the attestation, plus the vault documents the
 *  operator was looking at where those are already on file. There is no name
 *  field and there never will be: the backend takes the operator off the
 *  session and rejects an unknown key, so a caller cannot sign someone else's
 *  name to a compliance record. */
export interface RecordFicaCheckRequest {
  identityDocumentSeen: boolean;
  addressProofSeen: boolean;
  identityDocumentId?: string;
  addressDocumentId?: string;
}

export interface FicaCheckResponse {
  beneficialOwner: BeneficialOwnerView;
  correlationId: string;
}

/** Codes the route answers with, so a caller can say the right thing. */
export const FICA_CHECK_INCOMPLETE = 'FICA_ATTESTATION_INCOMPLETE';
export const FICA_CHECK_FORBIDDEN = 'NOT_AUTHORISED_TO_RECORD_FICA';
export const FICA_CHECK_ALREADY_VERIFIED = 'FICA_ALREADY_VERIFIED';
export const FICA_CHECK_NOTHING_TO_WITHDRAW = 'NO_MANUAL_FICA_TO_WITHDRAW';

/** Every read of the register PLUS everything that derives a FICA position from
 *  it: the trust itself, the trust list, the firm book deck, the risk-review
 *  queue and the dashboard summary all carry `initialFicaOnFile`, and this
 *  mutation is the one thing in the product that changes it. Missing one of
 *  them leaves a screen saying "outstanding" about a person who is not. */
function invalidateFicaReaders(
  qc: ReturnType<typeof useQueryClient>,
  trustId: string,
) {
  invalidateRegister(qc, trustId);
  void qc.invalidateQueries({ queryKey: ['trusts'] });
  void qc.invalidateQueries({ queryKey: ['dashboard'] });
  void qc.invalidateQueries({ queryKey: ['firm', 'book-summary'] });
  void qc.invalidateQueries({ queryKey: ['admin', 'risk-review'] });
}

/**
 * Record the firm's own FICA check for one person on the register.
 * POST /v1/beneficial-ownership/{id}/fica-check?trustId={trustId}.
 *
 * ── `useOperatorSession: true`, and why it is not optional ───────────────────
 * Same wall as `useLodgeMaster` above. The route requires step-up; a firm-act
 * token can never carry `mfa_challenge_at` (`mintFirmActToken` omits it by
 * design), so on an act-as session the POST would 403 STEP_UP_REQUIRED, the
 * global modal would challenge, mint a fresh SUPABASE token, and replay on the
 * unchanged act-as token, which 403s again. An infinite MFA loop the operator
 * reads as a dead button. Riding the operator's own session means their real
 * MFA challenge satisfies step-up, and the backend resolves the client trust
 * across the firm's grant-verified book and writes inside the client tenant.
 */
export function useRecordFicaCheck(trustId: string) {
  const qc = useQueryClient();
  return useMutation<FicaCheckResponse, ApiError, { id: string; body: RecordFicaCheckRequest }>({
    mutationFn: ({ id, body }) => {
      const search = new URLSearchParams({ trustId });
      return api<FicaCheckResponse>(
        `/api/v1/beneficial-ownership/${encodeURIComponent(id)}/fica-check?${search.toString()}`,
        { method: 'POST', body: { action: 'record', ...body }, useOperatorSession: true },
      );
    },
    onSuccess: () => invalidateFicaReaders(qc, trustId),
  });
}

/**
 * Withdraw a FICA check this firm recorded by hand, with the reason.
 *
 * The person goes back to outstanding and the trust's initial-FICA flag falls
 * with them. Nothing is deleted: the original confirmation stays on the record
 * and both events stand on the trust's audit chain.
 */
export function useWithdrawFicaCheck(trustId: string) {
  const qc = useQueryClient();
  return useMutation<FicaCheckResponse, ApiError, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => {
      const search = new URLSearchParams({ trustId });
      return api<FicaCheckResponse>(
        `/api/v1/beneficial-ownership/${encodeURIComponent(id)}/fica-check?${search.toString()}`,
        { method: 'POST', body: { action: 'withdraw', reason }, useOperatorSession: true },
      );
    },
    onSuccess: () => invalidateFicaReaders(qc, trustId),
  });
}

// ── LOD-01: download the register in the Master's own layout ──────────────────

/** Codes the export route answers with, so a caller can say the right thing. */
export const BO_REGISTER_EXPORT_EMPTY = 'NO_ACTIVE_BENEFICIAL_OWNERS';
export const BO_REGISTER_EXPORT_FORBIDDEN = 'NOT_AUTHORISED_TO_EXPORT_REGISTER';

/** The containers the export route serves. `.xlsx` is the register's own. */
export type MasterRegisterFormat = 'xlsx' | 'csv';

/**
 * Fetch the beneficial-ownership register as the Master's 23-column sheet and
 * hand it to the browser as a download.
 *
 * `.xlsx` BY DEFAULT (3 Sep 2026). This used to be CSV-only, because the
 * backend had no spreadsheet writer. It has one now, and the difference is not
 * cosmetic: in a workbook a thirteen-digit identity number stays text instead
 * of opening as `6.50326E+12`, and the two beneficial-ownership dates are real
 * dates. `format: 'csv'` still fetches the original file for anyone who wants
 * it.
 *
 * WHY THIS IS A RAW FETCH AND NOT `api()`. `api()` parses every response as
 * JSON; this route answers a binary workbook. The token order is NOT re-derived
 * here:
 * it comes from `bearerTokenForContext()`, the same single answer `api()` asks
 * for, so an act-as session downloads the CLIENT's register and an expired
 * credential is REFUSED rather than quietly falling back to the operator's own
 * tenant. That fallback is the wrong-tenant read `beneficiaries.ts` documents at
 * length; this is the same shape and must not drift from it.
 *
 * Errors are thrown as `ApiError` with the backend's own code, so the caller can
 * tell "nothing on the register yet" from "not your file to download".
 */
export async function downloadMasterRegister(args: {
  trustId: string;
  /** The trust's name as the caller already has it on screen. Used ONLY for the
   *  fallback filename below; the backend's own name still wins when it
   *  arrives. Optional so a caller without it still gets a dated file. */
  trustName?: string;
  /** Defaults to the workbook. `'csv'` asks for the original text file. */
  format?: MasterRegisterFormat;
}): Promise<void> {
  const format: MasterRegisterFormat = args.format ?? 'xlsx';
  const base = (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? '';
  const path =
    `/api/v1/trusts/${encodeURIComponent(args.trustId)}/beneficial-ownership/register-export?format=${format}`;
  const url = base ? `${base}${path}` : path;

  const { credential, token } = await bearerTokenForContext();
  if (credential.status === 'expired') {
    if (credential.kind === 'act-as') {
      throw new ApiError(
        401,
        { code: 'ACT_AS_EXPIRED' },
        'ACT_AS_EXPIRED',
        '/admin/client-book',
        'Your access to this client expired. Reopen the client to continue.',
      );
    }
    throw new ApiError(
      401,
      { code: 'IMPERSONATION_EXPIRED' },
      'IMPERSONATION_EXPIRED',
      undefined,
      'This impersonation expired. Start it again to continue.',
    );
  }
  const headers: Record<string, string> = {};
  if (token) headers['authorization'] = `Bearer ${token}`;

  const res = await fetch(url, { method: 'GET', headers });
  if (!res.ok) {
    let body: unknown = null;
    let code: string | undefined;
    try {
      body = await res.json();
      const obj = body as Record<string, unknown> | null;
      if (obj && typeof obj['code'] === 'string') code = obj['code'];
    } catch {
      body = null;
    }
    throw new ApiError(
      res.status,
      body,
      code,
      undefined,
      `Register export failed: ${res.status}${code ? ` ${code}` : ''}`,
    );
  }

  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') ?? '';
  const match = /filename="?([^"]+)"?/.exec(cd);
  // RULING 20 (Paul, 3 Sep 2026) - the generic name is not a fallback, it is a
  // second bug. `Content-Disposition` is not on the gateway's
  // `Access-Control-Expose-Headers`, so on a cross-origin build this header is
  // invisible to us however well the backend set it (an APIM policy change, not
  // this file's). Every export then landed as the same `bo-register-master.csv`,
  // and two trusts exported in one sitting are two files nobody can tell apart -
  // on a document that goes to the Master. So we build the same name the backend
  // builds, from the trust name the caller already has on screen and today's
  // date. The backend's own name still wins whenever it reaches us.
  const name = match?.[1] ?? localRegisterFilename(args.trustName, format);

  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // RULING 20 - DO NOT revoke here. `a.click()` only QUEUES the download; the
  // browser reads the blob afterwards, on its own schedule. Revoking in the same
  // tick (this used to sit in a `finally`) is a race whose loser is a person who
  // clicked Export, got no file and got no error either. One minute is far longer
  // than any browser takes to start reading, and the URL is released after it.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

/**
 * The download filename when the backend's own did not reach us. Mirrors
 * `backend/src/lib/bo-master-register.ts masterRegisterFilename` so the two
 * agree byte for byte on the same trust and day, and a person cannot tell which
 * side named their file.
 */
function localRegisterFilename(
  trustName: string | undefined,
  extension: MasterRegisterFormat,
): string {
  const slug = (trustName ?? '')
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .toLowerCase()
    .slice(0, 60);
  const day = new Date().toISOString().slice(0, 10);
  return `bo-register-master-${slug.length > 0 ? `${slug}-` : ''}${day}.${extension}`;
}
